import {
  AddItemButton,
  ArrayInput,
  AutocompleteArrayInput,
  BooleanInput,
  DateTimeInput,
  FormDataConsumer,
  InputProps,
  NumberInput,
  ReferenceArrayInput,
  SelectArrayInput,
  SimpleFormIterator,
  TextInput,
} from 'react-admin';
import { Typography } from '@mui/material';
import { useTranslate } from 'ra-core';
import * as React from 'react';
import _ from 'lodash';
import { daysOfWeek } from '../../lib/constants/selectChoices';

export const kioskConfigTransformer = (data: any) => {

  if (!data.screenSchedule) {

    data.schedule = null;

  } else {

    delete data.screenSchedule;

  }

  data = {
    ...data,
    homepage: {
      ...data.homepage,
      incidentCounter: {
        ...data.homepage?.incidentCounter,
        countFromDate: data.homepage?.incidentCounter?.countFromDate || null,
      },
    },
    manageAvailability: {
      ...data.manageAvailability,
      pin: data.manageAvailability?.pin?.toString() || null,
    },
  };

  if (data.exposeAssets.length > 0) {

    data.exposeAssets = data.exposeAssets.map((id: string) => ({
      _id: id,
      _ref: 'AssetGroup',
    }));

  }
  if (data.exposeLabels.length > 0) {

    data.exposeLabels = data.exposeLabels.map((id: string) => ({
      _id: id,
      _ref: 'AssetGroup',
    }));

  }
  if (data.zone?._id) {

    data.zone = { _id: data.zone._id, _ref: 'Zone' };

  }
  return data;

};
export const KioskConfigInput = (props: InputProps) => {

  const translate = useTranslate();
  return (
    <>
      <Typography variant="h6" >{translate('resources.customers.fields.kiosk_config.what_groups')}</Typography>
      <ReferenceArrayInput source={`${props.source}.exposeAssets`} reference="asset-groups" label="resources.asset-groups.name" >
        <AutocompleteArrayInput label="resources.asset-groups.name"/>
      </ReferenceArrayInput>

      <Typography variant="h6" >{translate('resources.customers.fields.kiosk_config.what_labels')}</Typography>
      <ReferenceArrayInput source={`${props.source}.exposeLabels`} reference="asset-groups">
        <AutocompleteArrayInput label="resources.asset-groups.name"/>
      </ReferenceArrayInput>

      <Typography variant="h6" >{translate('resources.customers.fields.kiosk_config.which_functions')}</Typography>
      <BooleanInput source={`${props.source}.alarm.enabled`} label="resources.customers.fields.kiosk_config.show_alarms"/>
      <BooleanInput source={`${props.source}.manageAvailability.enabled`} label="resources.customers.fields.kiosk_config.manage_availability"/>
      <BooleanInput source={`${props.source}.screenSchedule`} label="resources.customers.fields.kiosk_config.screen_schedule"/>
      <BooleanInput source={`${props.source}.homepage.incidentCounter.enabled`} label="resources.customers.fields.kiosk_config.incident_counter"/>

      <Typography variant="h6" >{translate('resources.customers.fields.kiosk_config.front_page')}</Typography>
      <TextInput source={`${props.source}.homepage.logo`} defaultValue={null} label="general.text.logo"/>
      <BooleanInput source={`${props.source}.homepage.anonymous`} label="resources.customers.fields.kiosk_config.anonymise_data"/>
      <FormDataConsumer>
        {({ formData }) => {

          const toReturn = [];
          if (_.get(formData, `${props.source}.homepage.incidentCounter.enabled`)) {

            toReturn.push(
              <>
                <Typography variant="h6" >{translate('resources.customers.fields.kiosk_config.incident_counter')}</Typography>
                <DateTimeInput
                  source={`${props.source}.homepage.incidentCounter.countFromDate`}
                  label="resources.customers.fields.kiosk_config.incident_counter_count_from_date"
                  shouldUnregister
                />
                <NumberInput
                  source={`${props.source}.homepage.incidentCounter.threshold`}
                  label="resources.customers.fields.kiosk_config.incident_counter_threshold"
                  helperText="resources.customers.fields.kiosk_config.incident_counter_threshold_helper_text"
                  shouldUnregister
                />
              </>,
            );

          }
          if (_.get(formData, `${props.source}.manageAvailability.enabled`)) {

            toReturn.push(
              <>
                <Typography variant="h6" >{translate('resources.customers.fields.kiosk_config.manage_availability')}</Typography>
                <NumberInput
                  source={`${props.source}.manageAvailability.pin`}
                  label="resources.customers.fields.kiosk_config.manage_availability_pin"
                  shouldUnregister
                />
                <BooleanInput
                  source={`${props.source}.manageAvailability.showAppBanner`}
                  label="resources.customers.fields.kiosk_config.manage_availability_app_hint"
                  defaultValue={true}
                  shouldUnregister
                />
                <BooleanInput
                  source={`${props.source}.manageAvailability.forceShowSearch`}
                  label="resources.customers.fields.kiosk_config.manage_availability_show_search"
                  shouldUnregister
                />
                <BooleanInput
                  source={`${props.source}.manageAvailability.forceShowLabelFilter`}
                  label="resources.customers.fields.kiosk_config.manage_availability_show_label_filter"
                  shouldUnregister
                />
              </>,
            );

          }

          if (_.get(formData, `${props.source}.screenSchedule`)) {

            toReturn.push(
              <>
                <Typography variant="h6" >{translate('resources.customers.fields.kiosk_config.screen_schedule')}</Typography>
                <ArrayInput source={`${props.source}.schedule.entries`} shouldUnregister>
                  <SimpleFormIterator
                    addButton={<div><AddItemButton/><Typography variant="body1" sx={{ display: 'inline', fontSize: '15px' }}>
                      {translate('resources.static-responders.fields.schedule_entry_add')}
                    </Typography></div>}>
                    <SelectArrayInput source="daysOfWeek" label="resources.static-responders.fields.days_of_week" choices={daysOfWeek}/>
                    <TextInput source="timeStart" label="resources.static-responders.fields.time_start"/>
                    <TextInput source="timeEnd" label="resources.static-responders.fields.time_end"/>
                  </SimpleFormIterator>
                </ArrayInput>
              </>,
            );

          }

          if (_.get(formData, `${props.source}.alarm.enabled`)) {

            toReturn.push(
              <>
                <Typography variant="h6" >{translate('resources.customers.fields.kiosk_config.show_alarms')}</Typography>
                <BooleanInput
                  source={`${props.source}.alarm.audio`}
                  label="resources.customers.fields.kiosk_config.alarm_audio"
                  shouldUnregister
                  defaultValue={true}
                />
                <BooleanInput
                  source={`${props.source}.alarm.flash`}
                  label="resources.customers.fields.kiosk_config.alarm_flash"
                  shouldUnregister
                  defaultValue={true}
                />
                <BooleanInput
                  source={`${props.source}.alarm.showPositionLabels`}
                  label="resources.customers.fields.kiosk_config.alarm_show_position_labels"
                  shouldUnregister
                  defaultValue={true}
                />
                <BooleanInput
                  source={`${props.source}.alarm.anonymous`}
                  label="resources.customers.fields.kiosk_config.alarm_anonymous"
                  shouldUnregister
                  defaultValue={true}
                />
              </>,
            );

          }
          return <>{toReturn}</>;

        }}
      </FormDataConsumer>
    </>
  );

};
